import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Button from 'react-bootstrap/Button';
import TextField from '@material-ui/core/TextField';
import { API_BASE } from '../config';
import { tr, lang } from '../i18n';
import { dayVariationOf } from './closeReportExcelHelper';
import './StationMap.css';

// ─── Settings ─────────────────────────────────────────────────────────────────
// Where each station is comes from its Zone ("672J+V7 Dar es Salaam") - the
// backend (station_map.py) turns that into coordinates and adds up the litres.
// Two views:
//   Sales       - litres sold in the chosen period AND the stock in the tanks
//                 now (POST /ak4tek/tanks/all, as the ATG page), both on each
//                 station's card on the map
//   Fuel shorts - each station's day variation from its close reports of the
//                 chosen days, added up (same figure as the DAY VARIATION in
//                 its Excel, summed over the period)
const TIME_ZONE = 'Africa/Dar_es_Salaam';
const TANZANIA_BOUNDS = [[-11.85, 29.3], [-0.95, 40.55]];

// OpenStreetMap tiles: free, no key. Their policy asks for the attribution
// below and normal (not bulk) use - fine for a staff dashboard.
const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors';

// Pins whose labels would overlap on screen (closer than this box, in pixels)
// are drawn as one group bubble. Zooming in splits them apart. The Sales card
// is a little taller (a sales line and a stock line).
const GROUP_W = 150;
const GROUP_H = { sales: 64, shorts: 58 };
// How far above the station's spot the card's popup opens.
const POPUP_Y = { sales: -60, shorts: -54 };

// Same limit as MAX_RANGE_DAYS in station_map.py.
const MAX_RANGE_DAYS = 93;
// Fuel shorts read every close report of the period (one per station per
// day, with all their details), so the period is kept shorter.
const MAX_SHORT_DAYS = 31;

// A tank reading older than this is shown as old (the station may be offline).
const STALE_HOURS = 24;

const MODE_KEY = 'station_map_mode';
// Leaderboard columns: which products are shown (petrol / diesel).
const PRODUCTS_KEY = 'station_map_products';
// Sales leaderboard sections: sales and/or stock.
const SECTIONS_KEY = 'station_map_sections';

// Stations without an ATG. Their tank numbers are not real readings, so their
// stock and fuel shorts are left out everywhere (their sales still count).
// Each entry is a word of the station's name ('IGOMA' matches "IGOMA STATION")
// or a serial number.
const NO_ATG_STATIONS = ['IGOMA', 'SENGERMA'];
const hasNoAtg = (s) => {
  const words = String(s.name || '').toUpperCase().split(/[^A-Z0-9]+/);
  return NO_ATG_STATIONS.some((k) => k === s.serial || words.includes(String(k).toUpperCase()));
};

// ─── Dates (Tanzania time) ────────────────────────────────────────────────────
const todayEAT = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date());

const shiftDay = (ymd, days) => {
  const d = new Date(`${ymd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const daysBetween = (from, to) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000) + 1;

const PRESETS = [
  { key: 'today',     label: 'Today',       range: (t) => [t, t] },
  { key: 'yesterday', label: 'Yesterday',   range: (t) => [shiftDay(t, -1), shiftDay(t, -1)] },
  { key: '7days',     label: 'Last 7 days', range: (t) => [shiftDay(t, -6), t] },
  { key: 'month',     label: 'This month',  range: (t) => [`${t.slice(0, 8)}01`, t] },
];

// Fuel shorts: close reports exist up to yesterday (today's closes tomorrow
// morning), so these end yesterday.
const SHORT_PRESETS = [
  { key: 'yesterday', label: 'Yesterday',   range: (t) => [shiftDay(t, -1), shiftDay(t, -1)] },
  { key: '7days',     label: 'Last 7 days', range: (t) => [shiftDay(t, -7), shiftDay(t, -1)] },
  { key: 'month',     label: 'This month',  range: (t) => { const y = shiftDay(t, -1); return [`${y.slice(0, 8)}01`, y]; } },
];

// ─── Formatting ───────────────────────────────────────────────────────────────
const fmt = (n) => Math.round(n || 0).toLocaleString('en-US');
// Litres: "1,234 L" in English, "1,234 ل" in Arabic (ar.js "{0} L").
const withL = (text) => tr('{0} L', text);
const fmtL = (n) => withL(fmt(n));
// 12k / 1.2M - the number alone (pins) and with the unit (bubbles).
const shortNum = (n) => {
  const v = n || 0;
  if (v >= 1e6) return `${+(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${+(v / 1e3).toFixed(v >= 1e4 ? 0 : 1)}k`;
  return `${Math.round(v)}`;
};
const shortL = (n) => withL(shortNum(n));
// Day variation: signed, 2 decimals as in the Excel; minus = fuel short.
const r2 = (n) => Math.round((n || 0) * 100) / 100;
const fmt2 = (n) => r2(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtVar = (n) => (n == null ? '—' : withL(`${r2(n) > 0 ? '+' : ''}${fmt2(n)}`));
// Whole litres, for the small pin labels and group bubbles.
const var0Num = (n) => {
  const v = Math.round(n) || 0;
  return `${v > 0 ? '+' : ''}${v.toLocaleString('en-US')}`;
};
const fmtVar0 = (n) => (n == null ? '—' : withL(var0Num(n)));
const varClass = (n) => (n == null || r2(n) === 0 ? '' : n < 0 ? 'sm-neg' : 'sm-pos');
const esc = (v) =>
  String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const parseTime = (v) => {
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : t;
};
// Dates in the page language ("02 Nov 2026" / "02 نوفمبر 2026", Latin digits).
const DATE_LOCALE = lang === 'ar' ? 'ar-u-nu-latn' : 'en-GB';
const fmtTime = (t) => (t == null ? '—'
  : new Date(t).toLocaleString(DATE_LOCALE, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }));
const isStale = (t) => t != null && Date.now() - t > STALE_HOURS * 3600 * 1000;
const fmtDay = (ymd) => (ymd
  ? new Date(`${ymd}T00:00:00Z`).toLocaleDateString(DATE_LOCALE, { timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric' })
  : '');

const placeOf = (s) => [s.district, s.region].filter(Boolean).join(', ');
const googleMapsUrl = (s) => `https://www.google.com/maps/search/?api=1&query=${s.lat},${s.lng}`;

// ─── Stock (for the Sales pin card) ──────────────────────────────────────────
// Same product words as product_group() in station_map.py.
const DIESEL_WORDS = ['DIESEL', 'AGO', 'GASOIL'];
const PETROL_WORDS = ['PETROL', 'PMS', 'UNLEADED', 'SUPER', 'MOGAS', 'GASOLINE', 'ULP', 'BENZINE', 'PREMIUM'];
const productGroup = (name) => {
  const words = String(name || '').toUpperCase().match(/[A-Z]+/g) || [];
  if (words.some((w) => DIESEL_WORDS.includes(w)) || (words.includes('GAS') && words.includes('OIL'))) return 'diesel';
  if (words.some((w) => PETROL_WORDS.includes(w))) return 'petrol';
  return 'other';
};

// Litres in the station's tanks now, per product. null = no ATG data.
function stockOf(tanks) {
  const list = (tanks || []).map((t) => ({
    group: productGroup(t.product_name),
    volume: num(t.fuel_volume),
    updated: parseTime(t.updated_at),
  }));
  if (!list.length) return null;
  const sum = (g) => list.filter((t) => t.group === g).reduce((a, t) => a + t.volume, 0);
  const times = list.map((t) => t.updated).filter((t) => t != null);
  return {
    petrol: sum('petrol'),
    diesel: sum('diesel'),
    other: sum('other'),
    // The oldest tank reading says how fresh the station's numbers are.
    updated: times.length ? Math.min(...times) : null,
  };
}

// ─── Fuel shorts of a period ─────────────────────────────────────────────────
// Every close report that STARTS between `from` and `to` (both days included):
// one per station per day, the latest generated_at when a day was synced
// twice (same rule as the Close Reports page). The server filters the dates
// and the user's stations; the exact days are checked here.
// Returns serial -> { PETROL: { variation }, DIESEL: { variation }, days }
// with each product's day variations added up over the period.
async function fetchShortsForRange(token, from, to, signal) {
  const params = new URLSearchParams({
    start_date: `${from}T00:00:00`,
    end_date: `${shiftDay(to, 7)}T23:59:59`, // a report started that day ends within a few days
    per_page: '100',
  });
  const latest = {};
  for (let page = 1; page <= 200; page++) {
    params.set('page', String(page));
    const res = await fetch(`${API_BASE}/ak4tek/close-reports?${params.toString()}`, {
      headers: { Authorization: token },
      signal,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const body = await res.json();
    const reports = body.reports || [];
    reports.forEach((r) => {
      const day = (r.start_date || '').slice(0, 10);
      if (!r.station_serial || day < from || day > to) return;
      const key = `${r.station_serial}|${day}`;
      const prev = latest[key];
      if (!prev || (r.generated_at || '') > (prev.generated_at || '')) latest[key] = r;
    });
    if (reports.length === 0 || !body.pagination || !body.pagination.has_next) break;
  }
  const out = {};
  Object.keys(latest).forEach((key) => {
    const serial = key.split('|')[0];
    const v = dayVariationOf(latest[key]);
    const o = out[serial] || (out[serial] = { PETROL: null, DIESEL: null, days: 0 });
    o.days += 1;
    ['PETROL', 'DIESEL'].forEach((g) => {
      if (!v[g]) return;
      o[g] = { variation: r2((o[g] ? o[g].variation : 0) + v[g].variation) };
    });
  });
  return out;
}

// ─── The two views of a station ───────────────────────────────────────────────
// stock: undefined = still loading, null = no ATG data.
const salesView = (s, stock) => {
  const noAtg = hasNoAtg(s);
  return { ...s, mode: 'sales', noAtg, stock: noAtg ? null : stock, weight: s.total };
};

// v: { PETROL, DIESEL, days } from fetchShortsForRange, null = no close report
// in the period, undefined = still loading.
function shortsView(s, v) {
  if (hasNoAtg(s)) {
    return {
      ...s, mode: 'shorts', noAtg: true, var: null, varPetrol: null, varDiesel: null, varTotal: null,
      days: 0, weight: 0,
    };
  }
  const p = v && v.PETROL ? v.PETROL.variation : null;
  const d = v && v.DIESEL ? v.DIESEL.variation : null;
  const total = p == null && d == null ? null : r2((p || 0) + (d || 0));
  return {
    ...s, mode: 'shorts', var: v, varPetrol: p, varDiesel: d, varTotal: total,
    days: v ? v.days : 0, weight: Math.abs(total || 0),
  };
}

// ─── Map pieces (plain Leaflet HTML) ─────────────────────────────────────────
const marker = (html, popupY) => L.divIcon({
  className: 'sm-marker', iconSize: [0, 0], iconAnchor: [0, 0], popupAnchor: [0, popupY], html,
});

// Sales card on the map:
//   UKONGA STATION
//   SALES  ● 1k   ● 3k      (petrol, diesel sold in the period)
//   STOCK  ● 10k  ● 15k     (petrol, diesel in the tanks now)
const pinPair = (petrol, diesel) =>
  `<span class="sm-pin-pair"><i class="sm-dot sm-p"></i>${shortNum(petrol)}</span>`
  + `<span class="sm-pin-pair"><i class="sm-dot sm-d"></i>${shortNum(diesel)}</span>`;

function salesPin(s) {
  let stock;
  if (s.stock === undefined) stock = '…';
  else if (!s.stock) stock = s.noAtg ? esc(tr('No ATG')) : '—';
  else stock = pinPair(s.stock.petrol, s.stock.diesel);
  const old = s.stock && isStale(s.stock.updated);
  return marker(`
      <div class="sm-pin${s.total > 0 ? '' : ' sm-pin--idle'}">
        <div class="sm-pin-chip">
          <span class="sm-pin-name">${esc(s.name)}</span>
          <span class="sm-pin-row sm-pin-sales"><span class="sm-pin-label">${esc(tr('Sales'))}</span>${pinPair(s.petrol, s.diesel)}</span>
          <span class="sm-pin-row${old ? ' sm-old' : ''}"><span class="sm-pin-label">${esc(tr('Stock'))}</span>${stock}</span>
        </div>
        <div class="sm-pin-tip"></div>
      </div>`, POPUP_Y.sales);
}

function shortsPin(s) {
  const has = s.varTotal != null;
  let value;
  if (s.noAtg) value = esc(tr('No ATG'));
  else if (s.var === undefined) value = '…';
  else if (!has) value = esc(tr('No report'));
  else value = `<span class="${varClass(s.varTotal)}">${esc(fmtVar0(s.varTotal))}</span>`;
  const part = (cls, v) => (v == null ? '' : `<span><i class="sm-dot ${cls}"></i>${esc(var0Num(v))}</span>`);
  return marker(`
      <div class="sm-pin${has ? '' : ' sm-pin--idle'}">
        <div class="sm-pin-chip">
          <span class="sm-pin-name">${esc(s.name)}</span>
          <span class="sm-pin-value">${value}</span>
          ${has ? `<span class="sm-pin-vars">${part('sm-p', s.varPetrol)}${part('sm-d', s.varDiesel)}</span>` : ''}
        </div>
        <div class="sm-pin-tip"></div>
      </div>`, POPUP_Y.shorts);
}

function groupIcon(group) {
  if (group[0].mode !== 'shorts') {
    const total = group.reduce((a, s) => a + s.total, 0);
    return marker(`<div class="sm-group"><b>${group.length}</b><span>${shortL(total)}</span></div>`, -30);
  }
  const withVar = group.filter((s) => s.varTotal != null);
  const net = withVar.reduce((a, s) => a + s.varTotal, 0);
  const cls = withVar.length && r2(net) < 0 ? ' sm-group--neg' : '';
  return marker(
    `<div class="sm-group${cls}"><b>${group.length}</b><span>${withVar.length ? esc(fmtVar0(net)) : '—'}</span></div>`, -30);
}

const popupRow = (label, value, cls = '') => `<tr class="${cls}"><td>${label}</td><td>${value}</td></tr>`;
const dotLabel = (cls, text) => `<i class="sm-dot ${cls}"></i>${esc(tr(text))}`;

function popupHead(s) {
  return `
      <div class="sm-pop-name">${esc(s.name)}</div>
      <div class="sm-pop-place">${esc(placeOf(s))}</div>`;
}

function popupFoot(s) {
  // <bdi dir="ltr">: codes and coordinates keep their order in Arabic too.
  const located = s.full_code
    ? `${esc(tr('Plus Code'))} <bdi dir="ltr">${esc(s.full_code)}</bdi>`
    : `<bdi dir="ltr">${s.lat.toFixed(6)}, ${s.lng.toFixed(6)}</bdi>`;
  return `
      <div class="sm-pop-foot">
        <span>${located}</span>
        <a href="${googleMapsUrl(s)}" target="_blank" rel="noreferrer">${esc(tr('Open in Google Maps'))}</a>
      </div>`;
}

function stockSection(stock, noAtg) {
  const title = esc(tr('Stock'));
  if (noAtg) return `<div class="sm-pop-sub">${title}: <span class="sm-sub-inline">${esc(tr('No ATG at this station'))}</span></div>`;
  if (stock === undefined) return `<div class="sm-pop-sub">${title}: …</div>`;
  if (!stock) return `<div class="sm-pop-sub">${title}: <span class="sm-sub-inline">${esc(tr('no ATG data'))}</span></div>`;
  const old = isStale(stock.updated);
  return `
      <div class="sm-pop-sub">${title}
        <span class="sm-sub-inline${old ? ' sm-old' : ''}">· ${esc(tr('ATG read'))} ${esc(fmtTime(stock.updated))}${old ? ` (${esc(tr('old'))})` : ''}</span>
      </div>
      <table>
        ${popupRow(dotLabel('sm-p', 'Petrol'), fmtL(stock.petrol))}
        ${popupRow(dotLabel('sm-d', 'Diesel'), fmtL(stock.diesel))}
        ${stock.other > 0 ? popupRow(dotLabel('sm-o', 'Other'), fmtL(stock.other)) : ''}
      </table>`;
}

function salesPopup(s, period) {
  return `
    <div class="sm-pop">
      ${popupHead(s)}
      <div class="sm-pop-sub sm-pop-sub--first">${esc(tr('Sales'))} <span class="sm-sub-inline">· ${esc(period)}</span></div>
      <table>
        ${popupRow(dotLabel('sm-p', 'Petrol'), fmtL(s.petrol))}
        ${popupRow(dotLabel('sm-d', 'Diesel'), fmtL(s.diesel))}
        ${s.other > 0 ? popupRow(dotLabel('sm-o', 'Other'), fmtL(s.other)) : ''}
        ${popupRow(esc(tr('Total')), fmtL(s.total), 'sm-pop-total')}
        ${popupRow(esc(tr('Amount (TZS)')), fmt(s.amount))}
        ${popupRow(esc(tr('Transactions')), fmt(s.sales_count))}
      </table>
      ${stockSection(s.stock, s.noAtg)}
      ${popupFoot(s)}
    </div>`;
}

// range: { label, days } of the chosen fuel-shorts period.
function shortsPopup(s, range) {
  const title = `
      <div class="sm-pop-sub sm-pop-sub--first">${esc(tr('Fuel shorts'))} <span class="sm-sub-inline">· ${esc(range.label)}</span></div>`;
  let body;
  if (s.noAtg) {
    body = `<div class="sm-pop-period">${esc(tr('No ATG at this station - its fuel shorts are not counted.'))}</div>`;
  } else if (s.var === undefined) {
    body = '<div class="sm-pop-period">…</div>';
  } else if (!s.var) {
    body = `<div class="sm-pop-period">${esc(tr(range.days > 1 ? 'No close report in this period.' : 'No close report for this day.'))}</div>`;
  } else {
    const groups = [['PETROL', 'sm-p', 'Petrol'], ['DIESEL', 'sm-d', 'Diesel']].filter(([g]) => s.var[g]);
    const cell = (v) => `<b class="${varClass(v)}">${esc(fmtVar(v))}</b>`;
    const rows = groups.map(([g, cls, label]) => popupRow(dotLabel(cls, label), cell(s.var[g].variation))).join('');
    const total = groups.length > 1 ? popupRow(esc(tr('Total')), cell(s.varTotal), 'sm-pop-total') : '';
    const daysNote = range.days > 1
      ? `<div class="sm-pop-note${s.days < range.days ? ' sm-old' : ''}">${esc(tr('Close reports: {0} of {1} days', s.days, range.days))}</div>`
      : '';
    body = groups.length ? `
      <table>${rows}${total}</table>
      ${daysNote}
      <div class="sm-pop-note">${esc(tr('Minus = fuel short.'))}</div>`
      : `<div class="sm-pop-period">${esc(tr('No petrol or diesel tank in the close report.'))}</div>`;
  }
  return `<div class="sm-pop">${popupHead(s)}${title}${body}${popupFoot(s)}</div>`;
}

const popupFor = (s, period, range) => (s.mode === 'shorts' ? shortsPopup(s, range) : salesPopup(s, period));

function groupPopup(group) {
  const shorts = group[0].mode === 'shorts';
  const rows = [...group]
    .sort((a, b) => b.weight - a.weight)
    .map((s) => (shorts
      ? popupRow(esc(s.name), s.noAtg ? esc(tr('No ATG')) : s.varTotal == null ? esc(tr('No report')) : `<span class="${varClass(s.varTotal)}">${esc(fmtVar(s.varTotal))}</span>`)
      : popupRow(esc(s.name), fmtL(s.total))))
    .join('');
  return `<div class="sm-pop"><div class="sm-pop-name">${group.length} ${esc(tr('stations'))}</div><table>${rows}</table></div>`;
}

// Greedy screen grouping: heaviest stations first (most litres sold, or the
// biggest short), each takes the unassigned stations whose labels would
// overlap its own (within GROUP_W x GROUP_H).
function groupStations(map, stations) {
  const zoom = map.getZoom();
  const boxH = stations.length && stations[0].mode === 'shorts' ? GROUP_H.shorts : GROUP_H.sales;
  const pts = stations
    .map((s) => ({ s, p: map.project([s.lat, s.lng], zoom) }))
    .sort((a, b) => b.s.weight - a.s.weight);
  const used = new Array(pts.length).fill(false);
  const groups = [];
  for (let i = 0; i < pts.length; i++) {
    if (used[i]) continue;
    used[i] = true;
    const group = [pts[i].s];
    for (let j = i + 1; j < pts.length; j++) {
      if (!used[j]
          && Math.abs(pts[i].p.x - pts[j].p.x) < GROUP_W
          && Math.abs(pts[i].p.y - pts[j].p.y) < boxH) {
        used[j] = true;
        group.push(pts[j].s);
      }
    }
    groups.push(group);
  }
  return groups;
}

const stockCell = (stock, key) => (stock === undefined ? '…' : stock ? fmt(stock[key]) : '—');

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function StationMap({ token, LogoutTimeout }) {
  const accessToken = token?.data?.access_token || token?.access_token;
  const today = useMemo(() => todayEAT(), []);
  const yesterday = useMemo(() => shiftDay(today, -1), [today]);

  // 'sales' = litres sold in the chosen period (+ stock now in the pin card),
  // 'shorts' = fuel shorts (day variation) from the close reports of a period.
  const [mode, setMode] = useState(() => {
    try { return sessionStorage.getItem(MODE_KEY) === 'shorts' ? 'shorts' : 'sales'; } catch (e) { return 'sales'; }
  });
  const [preset,       setPreset]       = useState('today');
  const [fromDate,     setFromDate]     = useState(today);
  const [toDate,       setToDate]       = useState(today);
  const [shortsFrom,   setShortsFrom]   = useState(yesterday);
  const [shortsTo,     setShortsTo]     = useState(yesterday);
  const [shortPreset,  setShortPreset]  = useState('yesterday');
  // Leaderboard columns: petrol and/or diesel (at least one stays on).
  const [products,     setProducts]     = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(PRODUCTS_KEY) || 'null');
      if (saved && (saved.petrol || saved.diesel)) return { petrol: !!saved.petrol, diesel: !!saved.diesel };
    } catch (e) { /* ignore */ }
    return { petrol: true, diesel: true };
  });
  // Sales leaderboard sections: sales and/or stock (at least one stays on).
  const [sections,     setSections]     = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(SECTIONS_KEY) || 'null');
      if (saved && (saved.sales || saved.stock)) return { sales: !!saved.sales, stock: !!saved.stock };
    } catch (e) { /* ignore */ }
    return { sales: true, stock: true };
  });
  const [search,       setSearch]       = useState('');
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [tankRows,     setTankRows]     = useState(null);
  const [variations,   setVariations]   = useState(null); // serial -> { PETROL, DIESEL }; null = loading
  const [varLoading,   setVarLoading]   = useState(false);
  const [message,      setMessage]      = useState('');
  const [showMissing,  setShowMissing]  = useState(false);
  const [reloadKey,    setReloadKey]    = useState(0);

  const mapDivRef  = useRef(null);
  const mapRef     = useRef(null);
  const layerRef   = useRef(null);
  const visibleRef = useRef([]);
  const periodRef  = useRef('');
  const shortDays  = shortsFrom && shortsTo && shortsFrom <= shortsTo ? daysBetween(shortsFrom, shortsTo) : 0;
  const shortLabel = shortsFrom === shortsTo ? fmtDay(shortsFrom) : `${fmtDay(shortsFrom)} → ${fmtDay(shortsTo)}`;
  const rangeRef   = useRef({ label: shortLabel, days: shortDays });
  rangeRef.current = { label: shortLabel, days: shortDays };
  const fittedRef  = useRef(false);
  const logoutRef  = useRef(LogoutTimeout);
  logoutRef.current = LogoutTimeout;
  // Read at request time. LogoutTimeout refreshes the login and replaces the
  // token; if the token were an effect dependency, every refresh would start
  // a new load, which refreshes again - an endless loop.
  const tokenRef   = useRef(accessToken);
  tokenRef.current = accessToken;
  const touchSession = () => { if (logoutRef.current) logoutRef.current(); };

  useEffect(() => {
    try { sessionStorage.setItem(MODE_KEY, mode); } catch (e) { /* ignore */ }
  }, [mode]);

  useEffect(() => {
    try { sessionStorage.setItem(PRODUCTS_KEY, JSON.stringify(products)); } catch (e) { /* ignore */ }
  }, [products]);

  useEffect(() => {
    try { sessionStorage.setItem(SECTIONS_KEY, JSON.stringify(sections)); } catch (e) { /* ignore */ }
  }, [sections]);

  // ── Markers ───────────────────────────────────────────────────────────
  const renderMarkers = useCallback(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();

    groupStations(map, visibleRef.current).forEach((group) => {
      if (group.length === 1) {
        const s = group[0];
        L.marker([s.lat, s.lng], { icon: s.mode === 'shorts' ? shortsPin(s) : salesPin(s), title: s.name, riseOnHover: true })
          .bindPopup(popupFor(s, periodRef.current, rangeRef.current), { maxWidth: 340 })
          .addTo(layer);
        return;
      }
      // Drawn at its heaviest station: those are always a label-box apart,
      // so bubbles and labels never sit on top of each other.
      const lead = group[0];
      const m = L.marker([lead.lat, lead.lng], { icon: groupIcon(group), title: `${group.length} ${tr('stations')}` });
      m.on('click', () => {
        const bounds = L.latLngBounds(group.map((s) => [s.lat, s.lng])).pad(0.3);
        const target = Math.min(map.getBoundsZoom(bounds), 18);
        if (target > map.getZoom()) {
          map.fitBounds(bounds, { maxZoom: 18 });
        } else {
          // Already as close as it gets (same spot): list them instead.
          m.bindPopup(groupPopup(group), { maxWidth: 340 }).openPopup();
        }
      });
      m.addTo(layer);
    });
  }, []);

  const fitTo = useCallback((list) => {
    const map = mapRef.current;
    if (!map) return;
    if (!list.length) {
      map.fitBounds(TANZANIA_BOUNDS);
      return;
    }
    map.fitBounds(L.latLngBounds(list.map((s) => [s.lat, s.lng])).pad(0.15), { maxZoom: 14 });
  }, []);

  // ── Map, once ─────────────────────────────────────────────────────────
  useEffect(() => {
    const map = L.map(mapDivRef.current, { minZoom: 4, zoomSnap: 0.5 });
    L.tileLayer(TILE_URL, { maxZoom: 19, attribution: TILE_ATTRIBUTION }).addTo(map);
    map.fitBounds(TANZANIA_BOUNDS);
    layerRef.current = L.layerGroup().addTo(map);
    map.on('zoomend', renderMarkers);
    mapRef.current = map;
    const t = setTimeout(() => map.invalidateSize(), 0);
    return () => {
      clearTimeout(t);
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, [renderMarkers]);

  // ── Data: stations, locations and sales ───────────────────────────────
  // Needed in both views (it is also the station list with locations).
  useEffect(() => {
    if (!fromDate || !toDate) return undefined;
    if (fromDate > toDate) {
      setMessage(tr('From date is after To date.'));
      return undefined;
    }
    if ((Date.parse(toDate) - Date.parse(fromDate)) / 86400000 > MAX_RANGE_DAYS) {
      setMessage(`${tr('Pick a shorter period')} (${MAX_RANGE_DAYS} ${tr('days or less')}).`);
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);
    setMessage('');

    const params = new URLSearchParams({ since_date: `${fromDate} 00:00:00`, to_date: `${toDate} 23:59:59` });
    fetch(`${API_BASE}/sales_reports/station_map?${params.toString()}`, {
      headers: { Authorization: tokenRef.current },
      signal: controller.signal,
    })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(res.status === 401
            ? tr('Session expired, please log in again.')
            : body.message || `HTTP ${res.status}`);
        }
        periodRef.current = fromDate === toDate ? fmtDay(fromDate) : `${fmtDay(fromDate)} → ${fmtDay(toDate)}`;
        if (mapRef.current) mapRef.current.closePopup(); // its numbers are for the old period
        setData(body);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') setMessage(err.message || tr('Failed to load the map data.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [fromDate, toDate, reloadKey]);

  // ── Data: stock now (Sales pin card) ──────────────────────────────────
  // Same request as the ATG page. Only the stations the map already has
  // (scoped by role on the server) are ever shown.
  useEffect(() => {
    if (mode !== 'sales') return undefined;
    const controller = new AbortController();
    fetch(`${API_BASE}/ak4tek/tanks/all`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: tokenRef.current },
      signal: controller.signal,
    })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(res.status === 401
            ? tr('Session expired, please log in again.')
            : body.message || `HTTP ${res.status}`);
        }
        setTankRows(Array.isArray(body.data) ? body.data : []);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setTankRows([]);
        setMessage(err.message || tr('Failed to load the tank data.'));
      });
    return () => controller.abort();
  }, [mode, reloadKey]);

  // ── Data: fuel shorts of the chosen period ────────────────────────────
  // From each station's close reports of those days: the same figure as the
  // DAY VARIATION column of its Excel close report, added up over the days.
  useEffect(() => {
    if (mode !== 'shorts' || !shortsFrom || !shortsTo) return undefined;
    if (shortsFrom > shortsTo) {
      setMessage(tr('From date is after To date.'));
      return undefined;
    }
    if (daysBetween(shortsFrom, shortsTo) > MAX_SHORT_DAYS) {
      setMessage(`${tr('Pick a shorter period')} (${MAX_SHORT_DAYS} ${tr('days or less')}).`);
      return undefined;
    }
    const controller = new AbortController();
    setMessage('');
    setVariations(null);
    setVarLoading(true);
    if (mapRef.current) mapRef.current.closePopup();
    fetchShortsForRange(tokenRef.current, shortsFrom, shortsTo, controller.signal)
      .then((bySerial) => setVariations(bySerial))
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setVariations({});
        setMessage(tr('Could not load the close reports of {0}: {1}',
          shortsFrom === shortsTo ? shortsFrom : `${shortsFrom} → ${shortsTo}`, err.message || err));
      })
      .finally(() => {
        if (!controller.signal.aborted) setVarLoading(false);
      });
    return () => controller.abort();
  }, [mode, shortsFrom, shortsTo, reloadKey]);

  const stations = useMemo(() => data?.stations || [], [data]);

  const tanksBySerial = useMemo(() => {
    const out = {};
    (tankRows || []).forEach((t) => {
      const serial = t.LicenseeTraSerialNo;
      if (serial) (out[serial] = out[serial] || []).push(t);
    });
    return out;
  }, [tankRows]);

  const viewStations = useMemo(
    () => (mode === 'shorts'
      ? stations.map((s) => shortsView(s, variations ? (variations[s.serial] || null) : undefined))
      : stations.map((s) => salesView(s, tankRows ? stockOf(tanksBySerial[s.serial]) : undefined))),
    [mode, stations, tankRows, tanksBySerial, variations],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toUpperCase();
    if (!q) return viewStations;
    return viewStations.filter((s) =>
      [s.name, s.serial, s.region, s.district, s.zone].some((v) => String(v || '').toUpperCase().includes(q)));
  }, [viewStations, search]);

  const located = useMemo(() => filtered.filter((s) => s.lat != null && s.lng != null), [filtered]);
  const missing = useMemo(() => filtered.filter((s) => s.lat == null || s.lng == null), [filtered]);

  // Leaderboard columns. With one product picked, the board shows and ranks
  // that product only.
  const showP = products.petrol;
  const showD = products.diesel;
  const both = showP && showD;
  const onlyOne = both ? null : (showP ? 'petrol' : 'diesel');
  const toggleProduct = (key) => {
    touchSession();
    setProducts((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      return next.petrol || next.diesel ? next : prev; // keep at least one
    });
  };

  const showSales = sections.sales;
  const showStock = sections.stock;
  const toggleSection = (key) => {
    touchSession();
    setSections((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      return next.sales || next.stock ? next : prev; // keep at least one
    });
  };

  // Sales: most sold first (most stock first when only Stock is shown).
  // Fuel shorts: biggest short first; no close report / no ATG last.
  const ranked = useMemo(() => {
    const list = [...located];
    if (mode !== 'shorts') {
      const key = showSales
        ? (s) => (onlyOne ? s[onlyOne] : s.total) || 0
        : (s) => (s.stock ? (onlyOne ? s.stock[onlyOne] : s.stock.petrol + s.stock.diesel) : -1);
      return list.sort((a, b) => key(b) - key(a));
    }
    const field = onlyOne === 'petrol' ? 'varPetrol' : onlyOne === 'diesel' ? 'varDiesel' : 'varTotal';
    const key = (s) => (s[field] == null ? Infinity : s[field]);
    return list.sort((a, b) => key(a) - key(b));
  }, [located, mode, onlyOne, showSales]);

  // Amount per product needs station_map.py to send amount_petrol / amount_diesel;
  // without it the Amount column shows only when both products are on.
  const hasProductAmounts = stations.some((s) => s.amount_petrol != null);
  const amountKey = both ? 'amount' : (hasProductAmounts ? `amount_${onlyOne}` : null);
  const salesCols = (showP ? 1 : 0) + (showD ? 1 : 0) + (both ? 1 : 0) + (amountKey ? 1 : 0);
  const stockCols = (showP ? 1 : 0) + (showD ? 1 : 0);

  // The leaderboard's Total row: only the stations in the table (on the map).
  // The cards above the map count every station, also those not on the map.
  const boardTotals = useMemo(() => ranked.reduce((t, s) => ({
    varPetrol: t.varPetrol + (s.varPetrol || 0),
    varDiesel: t.varDiesel + (s.varDiesel || 0),
    reports:   t.reports + (s.var ? 1 : 0),
  }), { varPetrol: 0, varDiesel: 0, reports: 0 }), [ranked]);
  const reportsOffMap = missing.filter((s) => s.var).length;

  const totals = useMemo(() => filtered.reduce((t, s) => {
    return {
      petrol:      t.petrol + (s.petrol || 0),
      diesel:      t.diesel + (s.diesel || 0),
      total:       t.total + (s.total || 0),
      amount:      t.amount + (s.amount || 0),
      count:       t.count + (s.sales_count || 0),
      varPetrol:   t.varPetrol + (s.varPetrol || 0),
      varDiesel:   t.varDiesel + (s.varDiesel || 0),
      short:       t.short + (s.varTotal != null && r2(s.varTotal) < 0 ? 1 : 0),
      reports:     t.reports + (s.var ? 1 : 0),
    };
  }, {
    petrol: 0, diesel: 0, total: 0, amount: 0, count: 0, varPetrol: 0, varDiesel: 0, short: 0, reports: 0,
  }), [filtered]);

  // Redraw on new data / search / view; frame the stations the first time.
  useEffect(() => {
    visibleRef.current = located;
    renderMarkers();
    if (!fittedRef.current && located.length) {
      fitTo(located);
      fittedRef.current = true;
    }
  }, [located, renderMarkers, fitTo]);

  // Typing a search frames what matches.
  const searchTouched = useRef(false);
  useEffect(() => {
    if (!searchTouched.current) return undefined;
    const t = setTimeout(() => fitTo(visibleRef.current), 400);
    return () => clearTimeout(t);
  }, [search, fitTo]);

  const chooseMode = (m) => {
    if (m === mode) return;
    touchSession();
    if (mapRef.current) mapRef.current.closePopup(); // it shows the other view's numbers
    setMode(m);
  };

  const choosePreset = (p) => {
    touchSession();
    const [from, to] = p.range(todayEAT());
    setPreset(p.key);
    setFromDate(from);
    setToDate(to);
  };

  const chooseShortPreset = (p) => {
    touchSession();
    const [from, to] = p.range(todayEAT());
    setShortPreset(p.key);
    setShortsFrom(from);
    setShortsTo(to);
  };

  const focusStation = (s) => {
    const map = mapRef.current;
    if (!map) return;
    mapDivRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    map.once('moveend', () => {
      L.popup({ maxWidth: 340, offset: [0, POPUP_Y[s.mode] || POPUP_Y.sales] })
        .setLatLng([s.lat, s.lng])
        .setContent(popupFor(s, periodRef.current, rangeRef.current))
        .openOn(map);
    });
    map.flyTo([s.lat, s.lng], Math.max(map.getZoom(), 15), { duration: 0.6 });
  };

  const isShorts = mode === 'shorts';
  const busy = loading || (isShorts && varLoading);
  const noneLocated = data && stations.length > 0 && stations.every((s) => s.lat == null);

  return (
    <div className="StationMapPage">
      <div className="sm-toolbar">
        <div className="btn-group sm-mode" role="group" aria-label={tr('Map shows')}>
          <Button size="sm" variant={!isShorts ? 'primary' : 'outline-primary'} onClick={() => chooseMode('sales')} aria-pressed={!isShorts}>
            <i className="fas fa-chart-line"></i> {tr('Sales')}
          </Button>
          <Button size="sm" variant={isShorts ? 'primary' : 'outline-primary'} onClick={() => chooseMode('shorts')} aria-pressed={isShorts}>
            <i className="fas fa-tint-slash"></i> {tr('Fuel shorts')}
          </Button>
        </div>

        {!isShorts ? (
          <>
            <div className="sm-presets">
              {PRESETS.map((p) => (
                <Button
                  key={p.key}
                  size="sm"
                  variant={preset === p.key ? 'primary' : 'outline-primary'}
                  onClick={() => choosePreset(p)}
                >
                  {tr(p.label)}
                </Button>
              ))}
            </div>
            <TextField
              label={tr('From')}
              type="date"
              size="small"
              value={fromDate}
              onChange={(e) => { setPreset('custom'); setFromDate(e.target.value); }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={tr('To')}
              type="date"
              size="small"
              value={toDate}
              onChange={(e) => { setPreset('custom'); setToDate(e.target.value); }}
              InputLabelProps={{ shrink: true }}
            />
          </>
        ) : (
          <>
            <div className="sm-presets">
              {SHORT_PRESETS.map((p) => (
                <Button
                  key={p.key}
                  size="sm"
                  variant={shortPreset === p.key ? 'primary' : 'outline-primary'}
                  onClick={() => chooseShortPreset(p)}
                >
                  {tr(p.label)}
                </Button>
              ))}
            </div>
            <TextField
              label={tr('From')}
              type="date"
              size="small"
              value={shortsFrom}
              onChange={(e) => { setShortPreset('custom'); setShortsFrom(e.target.value); }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: today }}
            />
            <TextField
              label={tr('To')}
              type="date"
              size="small"
              value={shortsTo}
              onChange={(e) => { setShortPreset('custom'); setShortsTo(e.target.value); }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: today }}
            />
          </>
        )}

        <TextField
          className="sm-search"
          label={tr('Search station / region')}
          size="small"
          value={search}
          onChange={(e) => { searchTouched.current = true; setSearch(e.target.value); }}
        />
        <div className="sm-actions">
          <Button size="sm" variant="outline-secondary" onClick={() => fitTo(located)}>{tr('Fit all')}</Button>
          <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => { touchSession(); setReloadKey((k) => k + 1); }}>
            {tr('Refresh')}
          </Button>
        </div>
      </div>

      {message && <div className="sm-message">{message}</div>}

      {!isShorts ? (
        <div className="sm-summary">
          <div className="sm-card"><span><i className="sm-dot sm-p" />{tr('Petrol')}</span><b>{fmtL(totals.petrol)}</b></div>
          <div className="sm-card"><span><i className="sm-dot sm-d" />{tr('Diesel')}</span><b>{fmtL(totals.diesel)}</b></div>
          <div className="sm-card"><span>{tr('Total')}</span><b>{fmtL(totals.total)}</b></div>
          <div className="sm-card"><span>{tr('Amount (TZS)')}</span><b>{fmt(totals.amount)}</b></div>
          <div className="sm-card"><span>{tr('Transactions')}</span><b>{fmt(totals.count)}</b></div>
          <div className="sm-card"><span>{tr('Stations on map')}</span><b>{located.length} / {filtered.length}</b></div>
        </div>
      ) : (
        <div className="sm-summary">
          <div className="sm-card" title={tr('All stations together. Minus = fuel short.')}>
            <span><i className="sm-dot sm-p" />{tr('Petrol short')}</span>
            <b className={variations ? varClass(totals.varPetrol) : ''}>{variations ? fmtVar(totals.varPetrol) : '…'}</b>
          </div>
          <div className="sm-card" title={tr('All stations together. Minus = fuel short.')}>
            <span><i className="sm-dot sm-d" />{tr('Diesel short')}</span>
            <b className={variations ? varClass(totals.varDiesel) : ''}>{variations ? fmtVar(totals.varDiesel) : '…'}</b>
          </div>
          <div className="sm-card" title={tr('All stations together. Minus = fuel short.')}>
            <span>{tr('Total short')}</span>
            <b className={variations ? varClass(totals.varPetrol + totals.varDiesel) : ''}>
              {variations ? fmtVar(totals.varPetrol + totals.varDiesel) : '…'}
            </b>
          </div>
          <div className={`sm-card${totals.short ? ' sm-card--neg' : ''}`}>
            <span>{tr('Stations short')}</span>
            <b>{variations ? `${totals.short} / ${totals.reports}` : '…'}</b>
          </div>
          <div className="sm-card" title={shortLabel}><span>{tr('Close reports')}</span><b>{variations ? `${totals.reports} / ${filtered.filter((s) => !s.noAtg).length}` : '…'}</b></div>
        </div>
      )}

      <div className="sm-map-wrap">
        <div ref={mapDivRef} className="sm-map" />
        {busy && <div className="sm-overlay">{tr('Loading…')}</div>}
        {!busy && noneLocated && (
          <div className="sm-overlay sm-overlay--hint">
            {tr('No station has a location yet.')}<br />
            {tr("Put each station's Plus Code in its Zone, e.g.")} <code>672J+V7 Dar es Salaam</code>
          </div>
        )}
      </div>

      {ranked.length > 0 && (
        <div className="sm-board-bar">
          <span className="sm-board-title">{tr('Leaderboard')}</span>
          <div className="sm-board-filters">
            {!isShorts && (
              <div className="sm-cols" role="group" aria-label={tr('Show')}>
                <span className="sm-cols-label">{tr('Show')}:</span>
                <button
                  type="button"
                  className={`sm-col-toggle${showSales ? ' is-on' : ''}`}
                  aria-pressed={showSales}
                  onClick={() => toggleSection('sales')}
                >
                  {tr('Sales')}
                </button>
                <button
                  type="button"
                  className={`sm-col-toggle${showStock ? ' is-on' : ''}`}
                  aria-pressed={showStock}
                  onClick={() => toggleSection('stock')}
                >
                  {tr('Stock')}
                </button>
              </div>
            )}
            <div className="sm-cols" role="group" aria-label={tr('Columns')}>
              <span className="sm-cols-label">{tr('Columns')}:</span>
              <button
                type="button"
                className={`sm-col-toggle${showP ? ' is-on' : ''}`}
                aria-pressed={showP}
                onClick={() => toggleProduct('petrol')}
              >
                <i className="sm-dot sm-p" />{tr('Petrol')}
              </button>
              <button
                type="button"
                className={`sm-col-toggle${showD ? ' is-on' : ''}`}
                aria-pressed={showD}
                onClick={() => toggleProduct('diesel')}
              >
                <i className="sm-dot sm-d" />{tr('Diesel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {ranked.length > 0 && !isShorts && (
        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th rowSpan={2}>#</th>
                <th rowSpan={2}>{tr('Station')}</th>
                <th rowSpan={2}>{tr('Region')}</th>
                {showSales && <th colSpan={salesCols} className="sm-th-group">{tr('Sales')}</th>}
                {showStock && <th colSpan={stockCols} className="sm-th-group">{tr('Stock')}</th>}
              </tr>
              <tr>
                {showSales && showP && <th className="num"><i className="sm-dot sm-p" />{tr('Petrol (L)')}</th>}
                {showSales && showD && <th className="num"><i className="sm-dot sm-d" />{tr('Diesel (L)')}</th>}
                {showSales && both && <th className="num">{tr('Total (L)')}</th>}
                {showSales && amountKey && <th className="num">{tr('Amount (TZS)')}</th>}
                {showStock && showP && <th className="num"><i className="sm-dot sm-p" />{tr('Petrol (L)')}</th>}
                {showStock && showD && <th className="num"><i className="sm-dot sm-d" />{tr('Diesel (L)')}</th>}
              </tr>
            </thead>
            <tbody>
              {ranked.map((s, i) => {
                const old = s.stock && isStale(s.stock.updated) ? ' sm-old' : '';
                // Bold = the number the board is ranked by.
                const bold = (v, on) => (on ? <b>{v}</b> : v);
                const stockBold = !showSales && onlyOne;
                return (
                  <tr key={s.serial} onClick={() => focusStation(s)} title={tr('Show on map')}>
                    <td>{i + 1}</td>
                    <td>{s.name}</td>
                    <td>{placeOf(s)}</td>
                    {showSales && showP && <td className="num">{bold(fmt(s.petrol), onlyOne)}</td>}
                    {showSales && showD && <td className="num">{bold(fmt(s.diesel), onlyOne)}</td>}
                    {showSales && both && <td className="num"><b>{fmt(s.total)}</b></td>}
                    {showSales && amountKey && <td className="num">{fmt(s[amountKey])}</td>}
                    {showStock && (s.noAtg ? (
                      <td colSpan={stockCols} className="num sm-muted">{tr('No ATG')}</td>
                    ) : (
                      <>
                        {showP && <td className={`num sm-stock${old}`}>{bold(stockCell(s.stock, 'petrol'), stockBold)}</td>}
                        {showD && <td className={`num sm-stock${old}`}>{bold(stockCell(s.stock, 'diesel'), stockBold)}</td>}
                      </>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {ranked.length > 0 && isShorts && (
        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th>#</th>
                <th>{tr('Station')}</th>
                <th>{tr('Region')}</th>
                {showP && <th className="num"><i className="sm-dot sm-p" />{tr('Petrol short')}</th>}
                {showD && <th className="num"><i className="sm-dot sm-d" />{tr('Diesel short')}</th>}
                {both && <th className="num">{tr('Total short')}</th>}
              </tr>
            </thead>
            <tbody>
              {ranked.map((s, i) => {
                const valueCols = (showP ? 1 : 0) + (showD ? 1 : 0) + (both ? 1 : 0);
                const partial = shortDays > 1 && s.var && s.days < shortDays;
                return (
                  <tr key={s.serial} onClick={() => focusStation(s)} title={tr('Show on map')}>
                    <td>{i + 1}</td>
                    <td>
                      {s.name}
                      {partial && (
                        <span className="sm-days" title={tr('Close reports: {0} of {1} days', s.days, shortDays)}>
                          {' '}({s.days}/{shortDays})
                        </span>
                      )}
                    </td>
                    <td>{placeOf(s)}</td>
                    {s.noAtg ? (
                      <td colSpan={valueCols} className="sm-muted">{tr('No ATG')}</td>
                    ) : s.var === undefined ? (
                      <td colSpan={valueCols} className="sm-muted">…</td>
                    ) : !s.var ? (
                      <td colSpan={valueCols} className="sm-muted">
                        {tr(shortDays > 1 ? 'No close report in this period' : 'No close report for this day')}
                      </td>
                    ) : (
                      <>
                        {showP && <td className={`num ${varClass(s.varPetrol)}`}><b>{fmtVar(s.varPetrol)}</b></td>}
                        {showD && <td className={`num ${varClass(s.varDiesel)}`}><b>{fmtVar(s.varDiesel)}</b></td>}
                        {both && <td className={`num ${varClass(s.varTotal)}`}><b>{fmtVar(s.varTotal)}</b></td>}
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
            {variations && boardTotals.reports > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={3}>
                    {tr('Total')} ({boardTotals.reports} {tr('stations')})
                    {reportsOffMap > 0 && (
                      <span className="sm-sub-inline"> · {tr('{0} more not on map', reportsOffMap)}</span>
                    )}
                  </td>
                  {showP && <td className={`num ${varClass(boardTotals.varPetrol)}`}>{fmtVar(boardTotals.varPetrol)}</td>}
                  {showD && <td className={`num ${varClass(boardTotals.varDiesel)}`}>{fmtVar(boardTotals.varDiesel)}</td>}
                  {both && <td className={`num ${varClass(boardTotals.varPetrol + boardTotals.varDiesel)}`}>{fmtVar(boardTotals.varPetrol + boardTotals.varDiesel)}</td>}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {missing.length > 0 && (
        <div className="sm-missing">
          <Button size="sm" variant="outline-warning" onClick={() => setShowMissing((v) => !v)}>
            {showMissing ? tr('Hide') : tr('Show')} {tr('stations not on map')} ({missing.length})
          </Button>
          {showMissing && (
            <>
              <p className="sm-help">
                {tr('In Google Maps, open the station and copy its Plus Code with the town, e.g.')}{' '}
                <code>672J+V7 Dar es Salaam</code>{' '}
                {tr("- then put it in the station's Zone. A full code (6G5X672J+V7) or coordinates (-6.797813, 39.280688) also work.")}
              </p>
              <div className="sm-table-wrap">
                <table className="sm-table">
                  <thead>
                    <tr>
                      <th>{tr('Station')}</th>
                      <th>{tr('Zone now')}</th>
                      <th>{tr('Why not on map')}</th>
                      <th className="num">{isShorts ? tr('Total short') : tr('Total (L)')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {missing.map((s) => (
                      <tr key={s.serial} className="sm-row-static">
                        <td>{s.name}<div className="sm-sub">{s.serial}</div></td>
                        <td>{s.zone || '—'}</td>
                        <td>{s.location_error}</td>
                        <td className={`num ${isShorts ? varClass(s.varTotal) : ''}`}>
                          {isShorts ? (s.noAtg ? tr('No ATG') : s.var === undefined ? '…' : fmtVar(s.varTotal)) : fmt(s.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}